<!-- Accessible Spreadsheet Grid Component — Figma Parity, div-based ARIA grid -->
<div class="child-table-container" role="region" [attr.aria-label]="title || config.title || 'Data table'">

  <!-- Skip link to bypass grid (WCAG 2.4.1) -->
  <button type="button" class="skip-link sr-only" (click)="onSkipTable($event)">Skip table</button>

  <!-- Screen reader announcements: polite channel -->
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ a11yService.currentAnnouncement }}
  </div>
  <!-- Screen reader announcements: assertive channel (errors, critical) -->
  <div class="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
    {{ a11yService.currentAssertiveAnnouncement }}
  </div>

  <!-- Hidden instruction for column headers (read after column name via aria-describedby) -->
  <div id="ct-header-instruction" class="sr-only">Press Space to enter and manage column actions</div>

  <!-- Table header with title -->
  <header class="table-header" *ngIf="title || config.title">
    <h3 class="table-title">{{ title || config.title }}</h3>
    <a *ngIf="reportUrl" [href]="reportUrl" target="_blank" rel="noopener noreferrer"
       class="table-header__report-link"
       [attr.aria-label]="'Open full report for ' + (title || config.title)">
      Open Full Report
      <lucide-angular name="external-link" [size]="14" aria-hidden="true"></lucide-angular>
    </a>
  </header>

  <!-- ===== Toolbar (visible in every state except truly-empty editable) ===== -->
  <child-table-toolbar
    *ngIf="coverState !== 'empty'"
    [searchTerm]="searchTerm"
    [searchPlaceholder]="searchPlaceholder"
    [pageSize]="pageSize"
    [pageSizeOptions]="pageSizeOptions"
    [selectedCount]="selectedIds.length"
    [canMoveUp]="canMoveSelectedUp()"
    [canMoveDown]="canMoveSelectedDown()"
    [hasCellRange]="!!selectionService.cellRange"
    [editable]="config.editable !== false"
    [readonly]="!!config.readonly"
    [disabled]="pasteErrorLocked"
    [totalRows]="displayRows.length"
    [tableTitle]="config.title || ''"
    [columns]="columns"
    (searchChange)="onSearchChange($event)"
    (pageSizeChange)="onPageSizeChange($event)"
    (moveUp)="moveSelectedRowUp()"
    (moveDown)="moveSelectedRowDown()"
    (moveToPosition)="onToolbarMoveToPosition($event)"
    (copy)="onCopyAction()"
    (openImport)="onOpenImport()"
    (templateDownload)="onToolbarTemplateDownload()"
    (deleteSelected)="onDeleteSelected()"
    (clearSelection)="onClearCells()"
  ></child-table-toolbar>

  <!-- ===== Empty state CTA (editable, truly 0 rows — no data at all) ===== -->
  <output *ngIf="coverState === 'empty'"
       class="ct-cover ct-cover--empty" aria-live="polite">
    <h3 class="ct-cover__heading">No {{ title || config.title || 'records' }} yet</h3>
    <p class="ct-cover__hint">Add your first entry or import data to get started.</p>
    <div class="ct-cover__actions">
      <button type="button"
              class="ct-cover__btn"
              (click)="onAddRow()"
              (keydown.enter)="onAddRow()"
              [attr.aria-label]="'Add ' + (title || config.title || 'record')"
              tabindex="0">
        <span aria-hidden="true">+</span> Add {{ title || config.title || 'Row' }}
      </button>
      <button type="button"
              class="ct-cover__btn ct-cover__btn--secondary"
              (click)="onOpenImport()"
              (keydown.enter)="onOpenImport()"
              aria-label="Import data from file"
              tabindex="0">
        Import
      </button>
    </div>
  </output>

  <!-- ===== Filter empty state (rows exist but all filtered out) ===== -->
  <output *ngIf="coverState === 'filteredEmpty'"
       class="ct-cover ct-cover--filtered-empty" aria-live="polite">
    <lucide-angular name="search-x" [size]="48" [strokeWidth]="1" aria-hidden="true"></lucide-angular>
    <p class="ct-cover__heading">No matching records</p>
    <p class="ct-cover__hint">
      {{ totalRowCount }} record{{ totalRowCount === 1 ? '' : 's' }} exist{{ totalRowCount === 1 ? 's' : '' }} but none match your current filters.
    </p>
    <ul class="ct-filter-chips" aria-label="Active filters">
      <li *ngIf="searchTerm">
        <button type="button" class="ct-filter-chip"
                (click)="clearSearchTerm()"
                [attr.aria-label]="'Remove search filter: ' + searchTerm">
          <lucide-angular name="search" [size]="12" aria-hidden="true"></lucide-angular>
          <span class="ct-filter-chip__label">"{{ searchTerm }}"</span>
          <lucide-angular name="x" [size]="12" aria-hidden="true"></lucide-angular>
        </button>
      </li>
      <li *ngFor="let filter of activeFilters">
        <button type="button" class="ct-filter-chip"
                (click)="removeFilter(filter.column)"
                [attr.aria-label]="'Remove filter: ' + getFilterLabel(filter)">
          <lucide-angular name="funnel" [size]="12" aria-hidden="true"></lucide-angular>
          <span class="ct-filter-chip__label">{{ getFilterLabel(filter) }}</span>
          <lucide-angular name="x" [size]="12" aria-hidden="true"></lucide-angular>
        </button>
      </li>
    </ul>
    <button type="button" class="ct-cover__btn ct-cover__btn--secondary"
            (click)="clearAllFilters()" aria-label="Clear all filters">
      Clear all filters
    </button>
  </output>

  <!-- ===== Loading / searching cover ===== -->
  <output *ngIf="coverState === 'loading'" class="ct-cover ct-cover--loading" aria-live="polite">
    <div class="ct-cover__spinner" aria-hidden="true"></div>
    <p class="ct-cover__heading">Searching&hellip;</p>
    <p class="ct-cover__hint">Looking for matching records.</p>
  </output>

  <!-- ===== Import progress cover ===== -->
  <div *ngIf="coverState === 'importing'" class="ct-cover ct-cover--importing" aria-live="polite">
    <div class="ct-import-card">
      <div class="ct-import-card__header">
        <div class="ct-import-card__icon" aria-hidden="true">
          <lucide-angular [name]="importProgress?.phase === 'saving' ? 'cloud-upload' : 'database'"
                          [size]="24" [strokeWidth]="1.5"></lucide-angular>
        </div>
        <div class="ct-import-card__meta">
          <p class="ct-import-card__title">
            {{ importProgress?.phase === 'saving' ? 'Saving to server' : 'Importing rows' }}
          </p>
          <p class="ct-import-card__subtitle">
            <ng-container *ngIf="importProgress?.phase === 'adding'">
              {{ importProgress?.current }} of {{ importProgress?.total }} rows
            </ng-container>
            <ng-container *ngIf="importProgress?.phase === 'saving'">
              Batch {{ importProgress?.current }} of {{ importProgress?.total }}
            </ng-container>
          </p>
        </div>
      </div>
      <progress class="ct-progress-bar"
                [value]="importProgress?.current ?? 0"
                [max]="importProgress?.total ?? 100"
                [attr.aria-valuetext]="importProgressLabel">
      </progress>
      <div class="ct-import-card__footer">
        <p class="ct-import-card__status">
          {{ importProgress?.phase === 'saving' ? 'Saving' : 'Uploading' }}: {{ importProgressPercent }}%
        </p>
        <button type="button" class="ct-import-card__cancel"
                (click)="cancelImport()"
                aria-label="Cancel import">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- ===== Recoverable error cover ===== -->
  <div *ngIf="coverState === 'error'" class="ct-cover ct-cover--error" role="alert" aria-live="assertive">
    <lucide-angular name="triangle-alert" [size]="48" [strokeWidth]="1" aria-hidden="true"></lucide-angular>
    <p class="ct-cover__heading">{{ recoverableError?.title }}</p>
    <p class="ct-cover__hint">{{ recoverableError?.message }}</p>
    <div class="ct-cover__actions">
      <button *ngIf="recoverableError?.retryAction" type="button" class="ct-cover__btn"
              (click)="retryError()" aria-label="Retry">
        <lucide-angular name="rotate-ccw" [size]="14" aria-hidden="true"></lucide-angular>
        Retry
      </button>
      <button type="button" class="ct-cover__btn ct-cover__btn--secondary"
              (click)="dismissError()" aria-label="Dismiss error">
        Dismiss
      </button>
    </div>
  </div>

  <!-- ===== Save-blocked banner (paste-lock / parent-save-needed) ===== -->
  <div *ngIf="coverState === 'saveBlocked'" class="ct-save-blocked" role="alert" aria-live="assertive">
    <lucide-angular name="triangle-alert" [size]="18" aria-hidden="true"></lucide-angular>
    <span>Editing is temporarily blocked. Resolve paste errors to continue.</span>
  </div>

  <!-- ===== Main grid (visible when ready or saveBlocked) ===== -->
  <div class="table-wrapper" *ngIf="coverState === 'ready' || coverState === 'saveBlocked'"
       (scroll)="onTableWrapperScroll($event)">
    <div class="child-table-app" role="application"
      [attr.aria-label]="(title || config.title || 'Spreadsheet') + ' editor'">

      <div #tableElement class="child-grid" [class.is-dragging]="selectionService.isDraggingSelection"
        role="grid"
        [attr.aria-rowcount]="totalRecordsCount + 1"
        [attr.aria-colcount]="getColumnCount()"
        aria-multiselectable="true"
        [attr.aria-label]="title || config.title || 'Spreadsheet data table'"
        [attr.aria-describedby]="getGridDescribedBy()"
        (focusout)="onTableFocusOut($event)"
        (focusin)="onGridFocusIn($event)">

        <!-- ===== Grid header ===== -->
        <div class="grid-header" role="rowgroup" (click)="clearCellRange()">
          <div class="grid-row" role="row" aria-rowindex="1" aria-label="Column headers"
            [style.grid-template-columns]="gridTemplateColumns">
            <!-- Row select header -->
            <div role="columnheader" class="grid-cell row-select-col"
              [id]="'row-select-header-' + tableInstanceId"
              aria-colindex="1"
              tabindex="-1" data-row-index="-1" data-col-index="-1"
              (keydown)="onHeaderKeydown($event, '', -1)">
              <input *ngIf="config.selectable" type="checkbox" [checked]="selectAll"
                [indeterminate]="selectedIds.length > 0 && selectedIds.length < displayRows.length"
                (change)="onSelectAll()" [attr.aria-label]="'Select all ' + displayRows.length + ' rows'" class="row-checkbox" />
              <span *ngIf="!config.selectable" class="sr-only">Row number</span>
            </div>
            <!-- Actions header -->
            <div *ngIf="config.showRowActions !== false" role="columnheader" class="grid-cell actions-col"
              [id]="'actions-header-' + tableInstanceId"
              [attr.aria-colindex]="(config.selectable || config.rowReorderable) ? 2 : 1"
              tabindex="0" data-row-index="-1" data-col-index="-2"
              (keydown)="onHeaderKeydown($event, '', -2)">
              <span>Actions</span>
            </div>
            <!-- Data column headers -->
            <div *ngFor="let column of visibleColumns; let colIndex = index; trackBy: trackByColumnKey"
              role="columnheader" [id]="column.key + '-header'"
              class="grid-cell column-header"
              [attr.aria-colindex]="getAriaColIndex(colIndex)"
              [attr.aria-sort]="column.sortable ? getSortAriaSort(column.key) : null"
              [attr.aria-describedby]="'ct-header-instruction'"
              [class.sortable]="column.sortable"
              [class.interaction-mode]="headerInteractionMode && headerInteractionColIndex === colIndex"
              [attr.tabindex]="colIndex === 0 && config.showRowActions === false ? 0 : -1"
              data-row-index="-1" [attr.data-col-index]="colIndex"
              (keydown)="onHeaderKeydown($event, column.key, colIndex)">
              <div class="th-content">
                <span class="column-label">{{ column.label }}</span>
                <div class="column-actions"
                  [attr.aria-hidden]="!(headerInteractionMode && headerInteractionColIndex === colIndex) || null">
                  <button *ngIf="column.sortable" type="button" class="sort-btn" (click)="onSort(column.key)"
                    [attr.aria-label]="'Sort by ' + column.label" tabindex="-1"
                    [attr.aria-hidden]="!(headerInteractionMode && headerInteractionColIndex === colIndex) || null"
                    (keydown)="onHeaderWidgetKeydown($event, column.key, colIndex, 'sort')">
                    <lucide-angular
                      [name]="sortColumn === column.key && sortDirection === 'desc' ? 'arrow-down' : 'arrow-up'"
                      [size]="14" aria-hidden="true"
                      [class.active]="sortColumn === column.key"></lucide-angular>
                  </button>
                  <button type="button" class="column-menu-btn"
                    [class.active]="menuService.openColumnMenu === column.key"
                    [class.has-filter]="hasColumnFilter(column.key)"
                    (click)="toggleColumnMenu(column.key, $event)"
                    [attr.aria-label]="'Open column filter options menu for ' + column.label + (hasColumnFilter(column.key) ? ' (filtered)' : '')"
                    [attr.aria-expanded]="menuService.openColumnMenu === column.key"
                    [attr.aria-controls]="'column-menu-' + column.key" aria-haspopup="menu" tabindex="-1"
                    [attr.aria-hidden]="!(headerInteractionMode && headerInteractionColIndex === colIndex) || null"
                    (keydown)="onHeaderWidgetKeydown($event, column.key, colIndex, 'menu')">
                    <lucide-angular [name]="hasColumnFilter(column.key) ? 'funnel' : 'list-filter'" [size]="14" aria-hidden="true"></lucide-angular>
                  </button>
                </div>
              </div>

              <!-- Column resize handle -->
              <span class="col-resize-handle" role="slider" aria-orientation="vertical" tabindex="-1"
                [attr.aria-label]="'Resize column ' + column.label"
                [attr.aria-hidden]="!(headerInteractionMode && headerInteractionColIndex === colIndex)"
                [attr.aria-valuemin]="getColumnMinWidthPx(column)" [attr.aria-valuemax]="getColumnMaxWidthPx(column)"
                [attr.aria-valuenow]="getColumnWidthPx(column)" (mousedown)="onResizeMouseDown($event, column.key)"
                (keydown)="onHeaderWidgetKeydown($event, column.key, colIndex, 'resize'); onResizeKeydown($event, column.key)"></span>

              <!-- Column dropdown menu -->
              <child-table-column-menu #columnMenuDiv *ngIf="menuService.openColumnMenu === column.key"
                [columnKey]="column.key"
                [column]="column"
                [isReadonly]="!!config.readonly"
                [uniqueValues]="getUniqueColumnValues(column.key)"
                (applyFilter)="onColumnFilterApply(column.key, $event)"
                (cancel)="cancelColumnMenu(column.key)"
              ></child-table-column-menu>
            </div>
          </div>
        </div>

        <!-- ===== Grid body ===== -->
        <div class="grid-body" role="rowgroup" (dragover)="dndService.onDragOver($event)" (drop)="dndService.onDrop($event)">
          <div *ngFor="let row of visibleRows; let rowIndex = index; trackBy: trackByRowId"
            class="grid-row" role="row"
            [attr.aria-rowindex]="getGlobalRowNumber(rowIndex) + 1" [attr.data-row-id]="row.id" [attr.data-row-index]="rowIndex"
            [attr.aria-label]="'Row ' + getGlobalRowNumber(rowIndex)"
            [style.grid-template-columns]="gridTemplateColumns"
            [class.selected]="selectedIds.includes(row.id)" [class.dirty]="row.dirty"
            [class.dragging]="dndService.draggingRowId === row.id" [class.drag-over]="dndService.dragOverRowId === row.id"
            [attr.aria-selected]="selectedIds.includes(row.id)"
            (dragenter)="dndService.onDragEnter($event, row.id)" (dragleave)="dndService.onDragLeave($event)">

            <!-- Smart row select cell: drag handle + row number / checkbox -->
            <div role="rowheader" class="grid-cell row-select-col"
              aria-colindex="1"
              [class.is-selected]="selectedIds.includes(row.id)"
              [attr.data-row-index]="rowIndex" [attr.data-row-id]="row.id"
              data-col-index="-1" tabindex="-1"
              [id]="'row-header-' + row.id"
              [attr.aria-label]="config.selectable ? ('Row ' + getGlobalRowNumber(rowIndex) + '. Press Space to select') : ('Row ' + getGlobalRowNumber(rowIndex))"
              (keydown)="onCellKeydown($event, row.id, '', rowIndex, -1)">
              <button *ngIf="config.rowReorderable" type="button" class="drag-handle" draggable="true"
                (dragstart)="dndService.onDragStart($event, row.id, rowIndex)" (dragend)="dndService.onDragEnd($event)"
                [attr.aria-label]="'Reorder row ' + getGlobalRowNumber(rowIndex)" aria-roledescription="drag handle"
                tabindex="-1">
                <lucide-angular name="grip-vertical" [size]="14" aria-hidden="true"></lucide-angular>
              </button>
              <span class="row-number" aria-hidden="true">{{ getGlobalRowNumber(rowIndex) }}</span>
              <input *ngIf="config.selectable" type="checkbox" class="row-checkbox"
                [checked]="selectedIds.includes(row.id)"
                (click)="onRowSelectClick($event, row.id, rowIndex)"
                [attr.aria-label]="'Select row ' + getGlobalRowNumber(rowIndex)" tabindex="-1" />
            </div>

            <!-- Actions cell (kebab menu) -->
            <div *ngIf="config.showRowActions !== false" role="gridcell" class="grid-cell actions-col"
              [attr.aria-colindex]="(config.selectable || config.rowReorderable) ? 2 : 1"
              [attr.aria-label]="'Row ' + getGlobalRowNumber(rowIndex) + ' actions'"
              [attr.data-row-index]="rowIndex" [attr.data-row-id]="row.id"
              data-col-index="-2" tabindex="-1"
              (keydown)="onCellKeydown($event, row.id, '', rowIndex, -2)">
              <button type="button" class="kebab-btn" (click)="toggleRowMenu(row.id, rowIndex, $event)"
                [attr.aria-label]="'Row ' + getGlobalRowNumber(rowIndex) + ' actions'" aria-haspopup="menu"
                [attr.aria-expanded]="menuService.openRowMenuId === row.id"
                [attr.aria-controls]="menuService.openRowMenuId === row.id ? 'ct-row-menu-' + tableInstanceId : null"
                tabindex="-1">
                <lucide-angular name="ellipsis-vertical" [size]="16" aria-hidden="true"></lucide-angular>
              </button>
            </div>

            <!-- Data cells -->
            <div *ngFor="let column of visibleColumns; let colIndex = index; trackBy: trackByColumnKey"
              role="gridcell" class="grid-cell data-cell"
              [attr.aria-colindex]="getAriaColIndex(colIndex)"
              [attr.aria-selected]="selectionService.isCellInRange(rowIndex, colIndex) || null"
              [attr.aria-required]="isColumnRequired(column) ? 'true' : null"
              [attr.aria-invalid]="(row.errors && row.errors[column.key]) ? 'true' : null"
              [attr.data-row-index]="rowIndex" [attr.data-col-index]="colIndex" [attr.data-row-id]="row.id"
              [attr.data-col-key]="column.key" [attr.tabindex]="rowIndex === 0 && colIndex === 0 ? 0 : -1"
              [attr.aria-label]="getCellAriaLabel(row, column, rowIndex)"
              [class.editable]="column.editable && !config.readonly"
              [class.is-readonly]="!column.editable || $any(column)._staticReadonly"
              [class.editing]="editingService.isEditing(row.id, column.key)"
              [class.has-error]="row.errors && row.errors[column.key] && row.errors[column.key] !== 'This field is required'"
              [class.cell-in-range]="selectionService.isCellInRange(rowIndex, colIndex)"
              [class.range-top]="selectionService.isRangeEdge(rowIndex, colIndex, 'top')"
              [class.range-bottom]="selectionService.isRangeEdge(rowIndex, colIndex, 'bottom')"
              [class.range-left]="selectionService.isRangeEdge(rowIndex, colIndex, 'left')"
              [class.range-right]="selectionService.isRangeEdge(rowIndex, colIndex, 'right')"
              (keydown)="onCellKeydown($event, row.id, column.key, rowIndex, colIndex)"
              (mousedown)="onCellMouseDown($event, rowIndex, colIndex)"
              (mouseenter)="onCellMouseEnter(rowIndex, colIndex)"
              (click)="onCellClick($event, row.id, column.key, rowIndex, colIndex)"
              (dblclick)="startEditing(row.id, column.key, $event)">
              <!-- Editing mode -->
              <ng-container *ngIf="editingService.isEditing(row.id, column.key); else displayMode">
                <ct-editor-host
                  [column]="column"
                  [value]="editingService.editingValue"
                  [rowId]="row.id"
                  [editHintId]="'edit-hint-' + tableInstanceId"
                  [hasError]="!!(row.errors && row.errors[column.key])"
                  [isRequired]="isColumnRequired(column)"
                  [errorId]="'cell-error-' + tableInstanceId + '-' + row.id + '-' + column.key"
                  (save)="onCellEditorSave($event, row.id, column.key)"
                  (cancel)="cancelEdit()"
                  (tabSave)="onCellEditorTabSave($event, row.id, column.key, rowIndex, colIndex)"
                ></ct-editor-host>
              </ng-container>
              <!-- Display mode -->
              <ng-template #displayMode>
                <span class="cell-value" [ngSwitch]="column.type" aria-hidden="true">
                  <ng-container *ngSwitchCase="'currency'">
                    {{ formatCurrency(getCellValue(row, column.key)) }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'date'">
                    {{ formatDate(getCellValue(row, column.key)) }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'datetime'">
                    {{ formatDatetime(getCellValue(row, column.key)) }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'select'">
                    <span class="select-display">
                      <span class="select-display-value">{{ getSelectLabel(column, getCellValue(row, column.key)) }}</span>
                      <span class="select-chevron-hitarea"
                        (mousedown)="$event.stopPropagation()"
                        (click)="startEditing(row.id, column.key, $event)">
                        <lucide-angular class="select-chevron" name="chevron-down" [size]="12" aria-hidden="true"></lucide-angular>
                      </span>
                    </span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'multiselect'">
                    <span class="multiselect-display">
                      <span class="multiselect-display-value">{{ getMultiselectLabel(column, getCellValue(row, column.key)) }}</span>
                      <span class="select-chevron-hitarea"
                        (mousedown)="$event.stopPropagation()"
                        (click)="startEditing(row.id, column.key, $event)">
                        <lucide-angular class="select-chevron" name="chevron-down" [size]="12" aria-hidden="true"></lucide-angular>
                      </span>
                    </span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'checkbox'">
                    <ct-checkbox-editor
                      [column]="column"
                      [value]="getCellValue(row, column.key)"
                      [rowId]="row.id"
                      (save)="onInlineCheckboxToggle($event, row.id, column.key)"
                    ></ct-checkbox-editor>
                  </ng-container>
                  <ng-container *ngSwitchCase="'textarea'">
                    <span class="cell-value-truncated" [title]="getCellValue(row, column.key)">
                      {{ getCellValue(row, column.key) }}
                    </span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'email'">
                    <a *ngIf="getCellValue(row, column.key)" class="cell-email-link"
                      [href]="'mailto:' + getCellValue(row, column.key)"
                      (click)="$event.stopPropagation()">{{ getCellValue(row, column.key) }}</a>
                    <span *ngIf="!getCellValue(row, column.key)"></span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'phone'">
                    {{ getCellValue(row, column.key) }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'pii'">
                    <span class="pii-masked">{{ getCellValue(row, column.key) ? '••••••••' : '' }}</span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'file'">
                    <span class="cell-file-display" [class.has-file]="getCellValue(row, column.key)"
                      [attr.aria-describedby]="getCellValue(row, column.key) ? 'ct-file-hint-replace-' + tableInstanceId : column.editable && !config.readonly ? 'ct-file-hint-upload-' + tableInstanceId : null">
                      <ng-container *ngIf="getCellValue(row, column.key) as val">
                        <lucide-angular name="file-text" [size]="14" aria-hidden="true"
                          style="flex-shrink:0;color:#546574"></lucide-angular>
                        <span class="cell-file-name-display">{{ val.name || val }}</span>
                        <button *ngIf="column.editable && !config.readonly"
                          type="button" class="cell-file-remove-btn"
                          [attr.aria-label]="'Remove file from ' + column.label"
                          (click)="onFileRemove(row.id, column.key, $event)">
                          <lucide-angular name="trash-2" [size]="14" aria-hidden="true"></lucide-angular>
                        </button>
                      </ng-container>
                      <ng-container *ngIf="!getCellValue(row, column.key) && column.editable && !config.readonly">
                        <lucide-angular name="upload" [size]="14" aria-hidden="true" style="color:#546574"></lucide-angular>
                      </ng-container>
                    </span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'formula'">
                    <span class="cell-formula">{{ getCellValue(row, column.key) }}</span>
                  </ng-container>
                  <ng-container *ngSwitchDefault>
                    {{ getCellValue(row, column.key) }}
                  </ng-container>
                </span>
              </ng-template>
              
              <span *ngIf="row.errors && row.errors[column.key] && row.errors[column.key] !== 'This field is required'" class="error-indicator"
                [id]="'cell-error-' + tableInstanceId + '-' + row.id + '-' + column.key"
                [attr.title]="row.errors[column.key]"
                role="img" [attr.aria-label]="'Error: ' + row.errors[column.key]">
                <lucide-angular name="circle-alert" [size]="14" aria-hidden="true"></lucide-angular>
              </span>

              <!-- Required indicator (when empty) -->
              <span *ngIf="isColumnRequired(column)" class="required-indicator" aria-hidden="true">
                <lucide-angular name="asterisk" [size]="12"></lucide-angular>
              </span>

              <!-- Read-only indicator -->
              <span *ngIf="!column.editable || $any(column)._staticReadonly" class="readonly-indicator" aria-hidden="true">
                <lucide-angular name="lock" [size]="12"></lucide-angular>
              </span>
            </div>
          </div>

          <!-- Empty state (read-only) -->
          <div *ngIf="displayRows.length === 0 && (config.editable === false || config.readonly)" class="grid-row empty-row" role="row">
            <div role="gridcell" class="grid-cell" style="grid-column: 1 / -1">
              <div class="empty-state" role="status" aria-live="polite">{{ config.emptyMessage || 'No records found' }}</div>
            </div>
          </div>
        </div>

      </div>

      <!-- Add row button (outside role="grid" so it's in the natural Tab flow) -->
      <div *ngIf="isEditable" class="add-row-container">
        <button type="button" class="add-row-btn" (click)="onAddRow()" aria-label="Add new row" tabindex="0">
          <span class="add-icon" aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  </div>

  <!-- ===== Pagination footer (visible when grid is shown) ===== -->
  <child-table-pagination #paginationRef
    *ngIf="coverState === 'ready' || coverState === 'saveBlocked'"
    [currentPage]="currentPage"
    [totalPages]="totalPages"
    [paginationStart]="paginationStart"
    [paginationEnd]="paginationEnd"
    [totalRecords]="config?.serverSidePagination ? (totalRecords ?? displayRows.length) : displayRows.length"
    [editable]="config.editable !== false"
    [readonly]="!!config.readonly"
    [hasUnsavedChanges]="showAutoSaveStatus ? hasUnsavedChanges : false"
    [lastSaved]="showAutoSaveStatus ? lastUpdated : null"
    [saving]="showAutoSaveStatus ? isSaving : false"
    (prev)="prevPage()"
    (next)="nextPage()"
    (manualSave)="onManualSave()"
  ></child-table-pagination>

  <!-- Table description (read once on initial focus via aria-describedby) -->
  <div *ngIf="config.description" [id]="'table-desc-' + tableInstanceId" class="sr-only">
    {{ config.description }}
  </div>

  <!-- SR-only grid usage instructions (discoverable via aria-describedby) -->
  <div [id]="'grid-instructions-' + tableInstanceId" class="sr-only">
    Spreadsheet grid. Use arrow keys to move between cells. Enter or F2 to edit a cell. Escape to cancel.
    Use Left Arrow to reach row selection. Press Space to select rows.
    In the header row, press Space to access column actions, Enter to sort.
    Tab moves between header and data areas. Shift plus arrow keys to extend selection.
  </div>

  <!-- Hidden hint for editing cells (WCAG 4.1.2) -->
  <div [id]="'edit-hint-' + tableInstanceId" class="sr-only">Press Enter to save, Escape to cancel</div>
  <div [id]="'ct-file-hint-upload-' + tableInstanceId" class="sr-only">Press Enter to upload a file</div>
  <div [id]="'ct-file-hint-replace-' + tableInstanceId" class="sr-only">Press Delete to remove file, or Enter to replace</div>


  <!-- Skip link target -->
  <div [id]="'after-table-' + tableInstanceId" tabindex="-1"></div>

  <!-- ===== Row context menu ===== -->
  <child-table-row-menu #rowMenuComp *ngIf="menuService.openRowMenuId"
    [menuId]="'ct-row-menu-' + tableInstanceId"
    [rowIndex]="menuService.openRowMenuIndex"
    [items]="menuService.rowMenuItems"
    (action)="onRowAction($event)"
    (close)="closeRowMenu()"
  ></child-table-row-menu>

  <!-- ===== Custom Move Modal ===== -->
  <child-table-custom-move *ngIf="customMoveOpen"
    [maxRows]="displayRows.length"
    (move)="onCustomMoveSubmit($event)"
    (close)="closeCustomMove()"
  ></child-table-custom-move>

  <!-- ===== Alert Modal (WCAG AA alertdialog) ===== -->
  <div *ngIf="alertModal" class="ct-alert-overlay"
    role="presentation" (keydown)="$event.stopPropagation()">
    <dialog class="ct-alert-modal" open
      role="alertdialog"
      aria-modal="true"
      [attr.aria-labelledby]="'ct-alert-title-' + tableInstanceId"
      [attr.aria-describedby]="'ct-alert-desc-' + tableInstanceId"
      (click)="$event.stopPropagation()"
      (keydown)="onAlertModalKeydown($event)">
      <div class="ct-alert-content">
        <div class="ct-alert-icon"
          [class.ct-alert-icon--info]="alertModal.icon === 'info'" aria-hidden="true">
          <lucide-angular *ngIf="alertModal.icon === 'warning'" name="triangle-alert" [size]="24" aria-hidden="true"></lucide-angular>
          <lucide-angular *ngIf="alertModal.icon === 'info'" name="info" [size]="24" aria-hidden="true"></lucide-angular>
        </div>
        <h2 [id]="'ct-alert-title-' + tableInstanceId">{{ alertModal.title }}</h2>
        <p [id]="'ct-alert-desc-' + tableInstanceId">{{ alertModal.message }}</p>
        <button #alertModalDismissBtn type="button" class="btn-primary ct-alert-dismiss-btn"
          (click)="alertModal.onDismiss()">
          {{ alertModal.buttonLabel }}
        </button>
      </div>
    </dialog>
  </div>

  <!-- ===== Import Modal ===== -->
  <ct-import-modal *ngIf="openImportModal"
    [tableKey]="tableKey"
    [tableTitle]="tableTitle || title"
    [fields]="importModalColumns"
    (importComplete)="onImportModalComplete($event)"
    (closed)="onImportModalClose()"
  ></ct-import-modal>
</div>
